import { observer } from 'mobx-react-lite';

import { Columns } from 'components/base/Columns';
import { EditNumber } from 'components/forms/EditNumber';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';
import { Field } from 'components/forms/Field';
import { Segmented, type SegmentedOption } from 'components/forms/Segmented';
import { Switch } from 'components/forms/Switch';
import type { OutputMatcherFile, OutputMatcherProceed } from 'api/node_definitions/endpoints/types';

import type { EditableOutputMatcher, OverrideStatus } from './EditableOutputMatcher';

const OUTPUT_FILE_OPTIONS: EditSelectOptionList<OutputMatcherFile> = [
  { key: 'stdout.log', value: 'stdout.log', label: 'stdout.log' },
  { key: 'stderr.log', value: 'stderr.log', label: 'stderr.log' },
];

// Fail first — the default; Success forces exit 0. Fail accepts empty or any non-zero integer (not 0).
const OVERRIDE_OPTIONS: readonly SegmentedOption<OverrideStatus>[] = [
  { value: 'fail', label: 'Fail', intent: 'danger' },
  { value: 'success', label: 'Success', intent: 'success' },
];

const PROCEED_OPTIONS: readonly SegmentedOption<OutputMatcherProceed>[] = [
  { value: 'continue', label: 'Continue' },
  { value: 'abort', label: 'Abort' },
];

interface Props {
  readonly matcher: EditableOutputMatcher;
}

/**
 * The fields of a single output matcher, laid out as Match | Handle columns. A pure `observer` view over an
 * `EditableOutputMatcher`: each control reads a getter and writes a bound setter, no local state. Success disables the
 * return code and shows 0; Fail keeps it editable (empty or any non-zero integer — entering 0 switches to Success).
 */
export const OutputMatcherFields = observer<Props>(({ matcher }) => (
  <Columns>
    <Columns.Column heading="Match" subtitle="Which file to scan and what to look for.">
      <Field label="Output file name">
        <EditSelect value={matcher.outputFile} onChange={matcher.setOutputFile} options={OUTPUT_FILE_OPTIONS} />
      </Field>
      <Field label="Search string">
        <EditString value={matcher.searchString} onChange={matcher.setSearchString} />
      </Field>
      <Switch label="Case insensitive" checked={matcher.caseInsensitive} onChange={matcher.setCaseInsensitive} />
      <Switch label="Use regex" checked={matcher.useRegex} onChange={matcher.setUseRegex} helpText="Match the search string as a Go RE2 regular expression." />
    </Columns.Column>

    <Columns.Column heading="Handle" subtitle="What to do on a match.">
      <Field label="Override node instance status">
        <Segmented value={matcher.overrideStatus} onChange={matcher.setOverrideStatus} options={OVERRIDE_OPTIONS} ariaLabel="Override node instance status" />
      </Field>
      <Field
        label="Custom return code"
        helpText="Backend mapping: 0 → completed (Success), non-zero → failed, empty → keep the agent's exit code and status. Fail accepts any non-zero integer (including negatives) or empty."
      >
        <EditNumber value={matcher.customReturnCode} onChange={matcher.setCustomReturnCode} disabled={matcher.overrideStatus !== 'fail'} />
      </Field>
      <Field label="Proceed matching">
        <Segmented value={matcher.proceed} onChange={matcher.setProceed} options={PROCEED_OPTIONS} ariaLabel="Proceed matching" />
      </Field>
    </Columns.Column>
  </Columns>
));
